import { useQuery } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';

/** Zentrales fachliches Änderungsprotokoll (#1330).
 *
 * Gelesen wird über `AuditEventContext`, nicht über die primäre Entität —
 * dadurch erscheint ein Ereignis in ALLEN zugehörigen Ansichten. Eine Aufgabe,
 * die in einem Vorgang erstellt wurde, ist im Aufgaben- und im Vorgangs-Log
 * sichtbar, ohne doppelt gespeichert zu sein.
 */

/** Ein einzelner Feldunterschied innerhalb eines Ereignisses. */
export interface IAuditChange {
  field: string;
  label?: string | null;
  old?: unknown;
  new?: unknown;
  /** Klartext hinter einer ID, als Snapshot zum Zeitpunkt des Ereignisses. */
  oldLabel?: string | null;
  newLabel?: string | null;
  /** Gesetzt, wenn der Wert nicht im Klartext gespeichert wurde. */
  masked?: 'secret' | 'partial' | 'metadata' | 'truncated' | null;
  oldMeta?: { len: number; sha256: string } | null;
  newMeta?: { len: number; sha256: string } | null;
}

export type AuditActorType =
  | 'user'
  | 'client'
  | 'device'
  | 'system'
  | 'integration';

export type AuditCategory = 'domain' | 'generic' | 'technical' | 'legacy';

/** Was der Abfrageparameter `category` mitbekommt.
 *
 *  Mehrere Kategorien sind seit der Migration des Alt-Protokolls der
 *  Regelfall: die Ansicht zeigt `domain` und `legacy` nebeneinander (#1330).
 */
export type AuditCategoryParam = AuditCategory | AuditCategory[] | 'all';

/** Kategorien als Abfrageparameter — mehrere kommagetrennt, wie `query.py`
 *  sie erwartet. Eigene Funktion, damit die Regel an einer Stelle steht und
 *  geprüft werden kann. */
export const categoryParam = (
  category?: AuditCategoryParam
): string | undefined => {
  if (!category) return undefined;
  return Array.isArray(category) ? category.join(',') : category;
};

export interface IAuditEvent {
  idAuditEvent: number;
  OccurredAt: string;
  ActorType: AuditActorType;
  ActorId?: number | null;
  ActorLabelSnapshot?: string | null;
  Source: string;
  SourceDetail?: string | null;
  EventType: string;
  Category: AuditCategory;
  EntityType: string;
  EntityId: string;
  EntityLabelSnapshot?: string | null;
  CorrelationId: string;
  SequenceInCorrelation: number;
  Changes?: IAuditChange[] | null;
  Metadata?: Record<string, unknown> | null;
  SchemaVersion: number;
}

export interface IAuditPage {
  count: number;
  next: number;
  previous: number | null;
  results: IAuditEvent[];
}

export interface IAuditFilter {
  entityType: string;
  entityId: number | string;
  page?: number;
  rowsPerPage?: number;
  /** Ohne Angabe liefert das Backend nur `domain` — die fachlichen Ereignisse. */
  category?: AuditCategoryParam;
  actorType?: AuditActorType | '';
  eventType?: string;
  search?: string;
  /** Bereichsfilter, aufgelöst von `areaQuery`. Serverseitig, damit die
   *  Auswahl das ganze Protokoll durchsucht und nicht nur die geladene
   *  Seite (#1330). */
  areaLabel?: string;
  areaEntityTypes?: string;
  areaEventTypes?: string;
  /** Erst laden, wenn der Reiter geöffnet wurde (Lazy Load). */
  enabled?: boolean;
}

/** Audit-Einträge einer Entität, paginiert. */
export const useAuditLog = ({
  entityType,
  entityId,
  page = 1,
  rowsPerPage = 25,
  category,
  actorType,
  eventType,
  search,
  areaLabel,
  areaEntityTypes,
  areaEventTypes,
  enabled = true,
}: IAuditFilter) => {
  const params = new URLSearchParams({
    entityType,
    entityId: String(entityId),
    page: String(page),
    rowsPerPage: String(rowsPerPage),
  });
  const categoryValue = categoryParam(category);
  if (categoryValue) params.set('category', categoryValue);
  if (actorType) params.set('actorType', actorType);
  if (eventType) params.set('eventType', eventType);
  if (search) params.set('search', search);
  if (areaLabel) params.set('areaLabel', areaLabel);
  if (areaEntityTypes) params.set('areaEntityTypes', areaEntityTypes);
  if (areaEventTypes) params.set('areaEventTypes', areaEventTypes);

  const { data, isLoading, isFetching, refetch, isError } =
    useQuery<IAuditPage>({
      queryKey: [
        'audit',
        entityType,
        entityId,
        page,
        rowsPerPage,
        category,
        actorType,
        eventType,
        search,
        areaLabel,
        areaEntityTypes,
        areaEventTypes,
      ],
      queryFn: () => customFetcher({ endpoint: `/audit?${params.toString()}` }),
      enabled: enabled && !!entityType && !!entityId,
    });

  return {
    auditEvents: data?.results ?? [],
    auditCount: data?.count ?? 0,
    isLoadingAudit: isLoading,
    isFetchingAudit: isFetching,
    isErrorAudit: isError,
    refetchAudit: refetch,
  };
};

/** Filter der Protokollsuche über alle Entitäten (#1330, Phase 6d). */
export interface IAuditSearchFilter {
  page?: number;
  rowsPerPage?: number;
  category?: AuditCategoryParam;
  actorType?: AuditActorType | '';
  /** Ohne Angabe über alle Entitätsarten. */
  entityType?: string;
  eventType?: string;
  search?: string;
  /** `YYYY-MM-DD`; das Backend vergleicht gegen `OccurredAt`. */
  dateFrom?: string;
  dateTo?: string;
  enabled?: boolean;
}

/** Protokollsuche ohne festen Entitätsbezug.
 *
 * Bewusst ein eigener Hook und nicht `useAuditLog` mit leerer Id: dort ist der
 * Entitätsbezug Pflicht (`enabled` prüft ihn), und genau das soll hier nicht
 * gelten. Ein optionaler Parameter hätte den Aufrufern der Aktenansicht
 * erlaubt, den Bezug versehentlich zu verlieren — und damit das ganze
 * Protokoll in einen Vorgangs-Reiter zu holen.
 */
export const useAuditSearch = ({
  page = 1,
  rowsPerPage = 50,
  category,
  actorType,
  entityType,
  eventType,
  search,
  dateFrom,
  dateTo,
  enabled = true,
}: IAuditSearchFilter) => {
  const params = new URLSearchParams({
    page: String(page),
    rowsPerPage: String(rowsPerPage),
  });
  const categoryValue = categoryParam(category);
  if (categoryValue) params.set('category', categoryValue);
  if (actorType) params.set('actorType', actorType);
  if (entityType) params.set('entityType', entityType);
  if (eventType) params.set('eventType', eventType);
  if (search) params.set('search', search);
  // Bis-Datum auf das Tagesende ziehen, sonst fällt der gewählte Tag heraus.
  if (dateFrom) params.set('dateFrom', `${dateFrom} 00:00:00`);
  if (dateTo) params.set('dateTo', `${dateTo} 23:59:59`);

  const { data, isLoading, isFetching, refetch, isError } =
    useQuery<IAuditPage>({
      queryKey: [
        'auditSearch',
        page,
        rowsPerPage,
        category,
        actorType,
        entityType,
        eventType,
        search,
        dateFrom,
        dateTo,
      ],
      queryFn: () => customFetcher({ endpoint: `/audit?${params.toString()}` }),
      enabled,
    });

  return {
    auditEvents: data?.results ?? [],
    auditCount: data?.count ?? 0,
    isLoadingAudit: isLoading,
    isFetchingAudit: isFetching,
    isErrorAudit: isError,
    refetchAudit: refetch,
  };
};
